import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CpmButton, CpmErrorCard, CpmPageShell } from '../../../components/design-system';
import { useApiClient } from '../../../hooks/useApiClient';
import {
    cpmExcisePackKey,
    cpmExciseRankClearedCopy,
    cpmExciseRankFailCopy,
    cpmExciseRankSavedCopy,
    cpmExciseRefreshEmptyCopy,
    cpmExciseRefreshFailCopy,
    cpmExciseRefreshSuccessCopy,
    cpmExciseStaleAfterRefreshCopy,
    formatCpmCompactRankMl,
    formatCpmPackMl,
} from '../../../constants/centralProductMaster.constants';
import {
    createExciseCategory,
    deleteExciseCategory,
    refreshExciseCategoryVolumes,
    updateExciseCategory,
    updateExciseCategoryStatus,
    updateStateLiveStatus,
} from '../../../services/productMasterService';
import { useAppDispatch } from '../../../store';
import {
    fetchStateCategories,
    fetchStates,
    setSelectedStateName,
    upsertCategory,
    useCpmExciseCategory,
} from '../../../store/slices/cpmExciseCategorySlice';
import type {
    CpmExciseCategory,
    CpmExciseStandardVolume,
    CpmState,
    CpmStateFilterTab,
} from '../../../types/centralProductMaster.types';
import { getApiErrorMessage } from '../../../utils/api/ApiClient';
import { toast } from '../../../utils/toast';
import AddCategoryModal from './components/AddCategoryModal';
import DeleteCategoryConfirm from './components/DeleteCategoryConfirm';
import MarkNotLiveModal from './components/MarkNotLiveModal';
import StateDetailView from './components/StateDetailView';
import StateListTable from './components/StateListTable';

function masterEditorUsageHref(stateName: string, categoryId: string): string {
    return `/central-product-master/master-editor?level=stateProducts&state=${encodeURIComponent(stateName)}&exciseCategoryId=${encodeURIComponent(categoryId)}`;
}

export default function ExciseCategoryCuration() {
    const dispatch = useAppDispatch();
    const slice = useCpmExciseCategory();
    const [searchParams, setSearchParams] = useSearchParams();
    const { callApi, loading: mutating } = useApiClient({ toast: true });

    const [stateQuery, setStateQuery] = useState('');
    const [stateFilter, setStateFilter] = useState<CpmStateFilterTab>('all');
    const [flagState, setFlagState] = useState<CpmState | null>(null);
    const [addOpen, setAddOpen] = useState(false);
    const [deleteCat, setDeleteCat] = useState<CpmExciseCategory | null>(null);
    const [liveFailName, setLiveFailName] = useState<string | null>(null);

    const selectedName = searchParams.get('state');

    useEffect(() => {
        dispatch(fetchStates());
    }, [dispatch]);

    useEffect(() => {
        if (selectedName) {
            dispatch(setSelectedStateName(selectedName));
            dispatch(fetchStateCategories(selectedName));
        } else {
            dispatch(setSelectedStateName(null));
        }
    }, [dispatch, selectedName]);

    const selectedState = useMemo(
        () => slice.states.find((s) => s.name === selectedName) ?? null,
        [slice.states, selectedName]
    );

    const openState = (name: string) => {
        const p = new URLSearchParams(searchParams);
        p.set('state', name);
        setSearchParams(p, { replace: false });
    };

    const goBack = () => {
        const p = new URLSearchParams(searchParams);
        p.delete('state');
        setSearchParams(p, { replace: false });
        setAddOpen(false);
        setDeleteCat(null);
    };

    const refreshCategories = () => {
        if (selectedName) dispatch(fetchStateCategories(selectedName));
    };

    const refreshAfterCountChange = () => {
        dispatch(fetchStates());
        refreshCategories();
    };

    const handleToggleLive = async (state: CpmState) => {
        if (state.isLive) {
            setFlagState(state);
            return;
        }
        setLiveFailName(null);
        const result = await callApi(() =>
            updateStateLiveStatus({ state: state.name, isLive: true })
        );
        if (result == null) {
            setLiveFailName(state.name);
            return;
        }
        const hasCats = (state.categoryCount || 0) > 0;
        toast.success(
            hasCats
                ? `${state.name} is now live and already has curated categories — state-product rows will be created on the next run.`
                : `${state.name} is now live but has no curated category yet, so state-product creation stays skipped until you add one.`
        );
        dispatch(fetchStates());
    };

    const confirmUnlive = async () => {
        if (!flagState) throw new Error('No state selected');
        const name = flagState.name;
        const result = await callApi(() => updateStateLiveStatus({ state: name, isLive: false }));
        if (result == null) throw new Error('Unlive failed');
        toast.success(`${name} is no longer live. Its curated categories are kept.`);
        dispatch(fetchStates());
    };

    const handleCreate = async (category: string, subNames: string[]) => {
        if (!selectedName) throw new Error('No state selected');
        const result = await callApi(() =>
            createExciseCategory({
                state: selectedName,
                category,
                subCategories: subNames.map((name) => ({ name })),
            })
        );
        if (result == null) throw new Error('Create failed');
        refreshAfterCountChange();
    };

    const handleRename = async (cat: CpmExciseCategory, name: string) => {
        const usage = cat.usageCount ?? 0;
        const result = await callApi(() =>
            updateExciseCategory({ exciseCategoryId: cat._id, category: name })
        );
        if (result == null) return;
        toast.success(
            usage > 0
                ? `Renamed to \u201C${name}\u201D. ${usage.toLocaleString('en-IN')} linked StateProduct rows now show the new name.`
                : `Renamed to \u201C${name}\u201D.`
        );
        refreshCategories();
    };

    const handleToggleStatus = async (cat: CpmExciseCategory) => {
        const active = (cat.status || 'ACTIVE') === 'ACTIVE';
        const next = active ? 'INACTIVE' : 'ACTIVE';
        const result = await callApi(() =>
            updateExciseCategoryStatus({
                exciseCategoryId: cat._id,
                status: next,
            })
        );
        if (result == null) return;
        toast.success(
            active
                ? `${cat.category} is now INACTIVE — it stays on existing records but can\u2019t be assigned to new ones.`
                : `${cat.category} is active again.`
        );
        refreshAfterCountChange();
    };

    const handleAddSub = async (cat: CpmExciseCategory, name: string) => {
        const result = await callApi(() =>
            updateExciseCategory({
                exciseCategoryId: cat._id,
                addSubCategories: [{ name }],
            })
        );
        if (result == null) return;
        toast.success(`Added \u201C${name}\u201D under ${cat.category}.`);
        refreshAfterCountChange();
    };

    const handleRenameSub = async (cat: CpmExciseCategory, subId: string, name: string) => {
        const result = await callApi(() =>
            updateExciseCategory({
                exciseCategoryId: cat._id,
                renameSubCategories: [{ _id: subId, name }],
            })
        );
        if (result == null) return;
        toast.success(
            `Subcategory renamed to \u201C${name}\u201D. Linked products pick up the new name automatically.`
        );
        refreshCategories();
    };

    const handleRemoveSub = async (cat: CpmExciseCategory, subId: string) => {
        const sub = (cat.subCategories ?? []).find((s) => s._id === subId);
        const result = await callApi(() =>
            updateExciseCategory({
                exciseCategoryId: cat._id,
                removeSubCategoryIds: [subId],
            })
        );
        if (result == null) return;
        toast.success(`Removed \u201C${sub?.name ?? 'subcategory'}\u201D from ${cat.category}.`);
        refreshAfterCountChange();
    };

    const handleRefreshSizes = async (cat: CpmExciseCategory) => {
        try {
            const result = await refreshExciseCategoryVolumes({
                exciseCategoryId: cat._id,
            });
            dispatch(upsertCategory(result));
            const seen = result.seenVolumes ?? [];
            if (seen.length > 0) {
                toast.success(cpmExciseRefreshSuccessCopy(cat.category, seen.length));
            } else {
                toast.success(cpmExciseRefreshEmptyCopy(cat.category));
            }
            const seenKeys = new Set(
                seen.map((row) => cpmExcisePackKey(row.volumeMl, row.volumeUnit))
            );
            const stale = (result.standardVolumes ?? []).filter(
                (row) => !seenKeys.has(cpmExcisePackKey(row.volumeMl, row.volumeUnit))
            );
            if (stale.length > 0) {
                const goneLabel = stale
                    .map((row) => formatCpmPackMl(row.volumeMl, row.volumeUnit))
                    .join(', ');
                toast.error(cpmExciseStaleAfterRefreshCopy(goneLabel, cat.category));
            }
        } catch {
            toast.error(cpmExciseRefreshFailCopy(cat.category));
            throw new Error('refresh sizes failed');
        }
    };

    const handleSetStandardVolumes = async (
        cat: CpmExciseCategory,
        standardVolumes: CpmExciseStandardVolume[]
    ) => {
        try {
            const result = await updateExciseCategory({
                exciseCategoryId: cat._id,
                standardVolumes,
            });
            dispatch(upsertCategory(result));
            const ranks = [...(result.standardVolumes ?? [])].sort((a, b) => a.rank - b.rank);
            if (ranks.length === 0) {
                toast.success(cpmExciseRankClearedCopy(cat.category));
            } else {
                toast.success(cpmExciseRankSavedCopy(cat.category, formatCpmCompactRankMl(ranks)));
            }
        } catch (err) {
            const prev = [...(cat.standardVolumes ?? [])].sort((a, b) => a.rank - b.rank);
            const rankText = prev.length > 0 ? formatCpmCompactRankMl(prev) : null;
            toast.error(cpmExciseRankFailCopy(cat.category, rankText, getApiErrorMessage(err)));
            throw err;
        }
    };

    const handleDelete = async () => {
        if (!deleteCat || !selectedName) throw new Error('Nothing to delete');
        const name = deleteCat.category;
        const result = await callApi(() =>
            deleteExciseCategory({ exciseCategoryId: deleteCat._id })
        );
        if (result == null) throw new Error('Delete failed');
        toast.success(`Deleted \u201C${name}\u201D from ${selectedName}.`);
        refreshAfterCountChange();
    };

    const handleDisableInstead = async () => {
        if (!deleteCat) throw new Error('Nothing to disable');
        const cat = deleteCat;
        const usage = cat.usageCount ?? 0;
        const result = await callApi(() =>
            updateExciseCategoryStatus({
                exciseCategoryId: cat._id,
                status: 'INACTIVE',
            })
        );
        if (result == null) throw new Error('Disable failed');
        toast.success(
            `${cat.category} disabled. Its ${usage.toLocaleString('en-IN')} linked rows keep their history; it can\u2019t be assigned going forward.`
        );
        refreshAfterCountChange();
    };

    const isDetail = !!selectedName;

    const breadcrumb = (
        <div
            className="flex items-center"
            style={{
                gap: 8,
                fontSize: 12.5,
                fontWeight: 600,
                color: 'var(--cpm-text-secondary)',
            }}
        >
            <span>Admin</span>
            <span style={{ fontWeight: 100, color: 'var(--cpm-text-tertiary)' }}>/</span>
            <span>Catalog</span>
            <span style={{ fontWeight: 100, color: 'var(--cpm-text-tertiary)' }}>/</span>
            <Link
                to="/central-product-master"
                className="!text-[var(--cpm-text-secondary)] !no-underline hover:!text-[var(--cpm-brand-navy)]"
            >
                Central Product Master
            </Link>
            <span style={{ fontWeight: 100, color: 'var(--cpm-text-tertiary)' }}>/</span>
            <span className="!text-[var(--cpm-brand-navy)]" style={{ fontWeight: 800 }}>
                Excise Category Curation
            </span>
        </div>
    );

    if (slice.statesLoading === 'loading' && slice.states.length === 0) {
        return (
            <CpmPageShell gap={22} maxWidth={1280} variant="compactBottom">
                {breadcrumb}
                <div
                    className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] rounded-[16px] shadow-[var(--cpm-shadow-card)] overflow-hidden"
                    aria-busy="true"
                    aria-label="Loading states"
                >
                    <div
                        className="flex items-center bg-[var(--cpm-surface-subtle)] border-b border-[var(--cpm-border-default)]"
                        style={{ gap: 10, padding: '12px 18px' }}
                    >
                        <span className="cpm-spin" />
                        <span
                            className="uppercase text-[var(--cpm-text-tertiary)]"
                            style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em' }}
                        >
                            Loading states
                        </span>
                    </div>
                    {Array.from({ length: 9 }, (_, i) => (
                        <div
                            key={i}
                            className="grid items-center border-b border-[var(--cpm-border-default)]"
                            style={{
                                gridTemplateColumns:
                                    'minmax(0,1.4fr) 110px 70px minmax(0,1fr) 48px',
                                gap: 14,
                                padding: '14px 18px',
                            }}
                        >
                            <div className="flex items-center" style={{ gap: 10 }}>
                                <span
                                    className="cpm-sk"
                                    style={{ width: 9, height: 9, borderRadius: 999 }}
                                />
                                <span
                                    className="cpm-sk"
                                    style={{ height: 14, width: `${48 + ((i * 9) % 36)}%` }}
                                />
                            </div>
                            <span
                                className="cpm-sk"
                                style={{ height: 20, width: 88, borderRadius: 999 }}
                            />
                            <span className="cpm-sk" style={{ height: 12, width: 52 }} />
                            <span className="cpm-sk" style={{ height: 12, width: '76%' }} />
                            <span
                                className="cpm-sk justify-self-end"
                                style={{ height: 19, width: 32, borderRadius: 999 }}
                            />
                        </div>
                    ))}
                </div>
            </CpmPageShell>
        );
    }

    if (slice.statesLoading === 'error' && slice.states.length === 0) {
        return (
            <CpmPageShell gap={22} maxWidth={1280} variant="compactBottom">
                {breadcrumb}
                <CpmErrorCard
                    title="Couldn't load states"
                    body="The state list and curation status didn't come back. Nothing has changed — curated categories and live flags are as they were."
                    onRetry={() => dispatch(fetchStates())}
                    retryLabel="Try again"
                />
            </CpmPageShell>
        );
    }

    return (
        <CpmPageShell gap={22} maxWidth={1280} variant="compactBottom">
            {breadcrumb}

            {!isDetail ? (
                <StateListTable
                    states={slice.states}
                    stateQuery={stateQuery}
                    stateFilter={stateFilter}
                    onStateQueryChange={setStateQuery}
                    onStateFilterChange={setStateFilter}
                    onClearFilters={() => {
                        setStateFilter('all');
                        setStateQuery('');
                    }}
                    onOpenState={openState}
                    onToggleLive={handleToggleLive}
                    liveFailName={liveFailName}
                />
            ) : selectedState ? (
                <StateDetailView
                    state={selectedState}
                    categories={slice.categories}
                    loading={slice.categoriesLoading === 'loading'}
                    error={slice.categoriesError}
                    busy={mutating}
                    onBack={goBack}
                    onOpenAdd={() => setAddOpen(true)}
                    onRename={handleRename}
                    onToggleStatus={handleToggleStatus}
                    onAskDelete={setDeleteCat}
                    onAddSub={handleAddSub}
                    onRenameSub={handleRenameSub}
                    onRemoveSub={handleRemoveSub}
                    onRefreshSizes={handleRefreshSizes}
                    onSetStandardVolumes={handleSetStandardVolumes}
                    onRetry={refreshCategories}
                    onLicenseTypesSaved={() => {
                        dispatch(fetchStates());
                    }}
                />
            ) : slice.statesLoading === 'loading' || slice.states.length === 0 ? (
                <div
                    className="text-[var(--cpm-text-secondary)]"
                    style={{
                        fontSize: 13,
                        fontWeight: 600,
                        padding: '64px 0',
                        textAlign: 'center',
                    }}
                >
                    Loading state…
                </div>
            ) : (
                <div className="flex flex-col items-center" style={{ gap: 12, padding: '48px 0' }}>
                    <p className="m-0 !text-[14px] !font-semibold !text-[var(--cpm-text-secondary)]">
                        State &ldquo;{selectedName}&rdquo; was not found in the list.
                    </p>
                    <CpmButton variant="outline" onClick={goBack}>
                        Back to all states
                    </CpmButton>
                </div>
            )}

            <MarkNotLiveModal
                open={!!flagState}
                state={flagState}
                categoryCount={flagState?.categoryCount ?? 0}
                onClose={() => setFlagState(null)}
                onConfirm={confirmUnlive}
                confirming={mutating}
            />

            {selectedName ? (
                <AddCategoryModal
                    open={addOpen}
                    stateName={selectedName}
                    onClose={() => setAddOpen(false)}
                    onSave={handleCreate}
                    saving={mutating}
                />
            ) : null}

            <DeleteCategoryConfirm
                open={!!deleteCat}
                category={deleteCat}
                stateName={selectedName || ''}
                usageHref={
                    deleteCat && selectedName
                        ? masterEditorUsageHref(selectedName, deleteCat._id)
                        : '#'
                }
                onClose={() => setDeleteCat(null)}
                onConfirmDelete={handleDelete}
                onDisableInstead={handleDisableInstead}
                busy={mutating}
            />
        </CpmPageShell>
    );
}
